import { filter } from "@suse-o11y/filter";
import { isArray } from "@suse-o11y/is-array";
import { isPresent } from "@suse-o11y/is-present";
import { map } from "@suse-o11y/map";
import { pipe } from "@suse-o11y/pipe";
import {
	type QueryParamConfig,
	createEnumParam,
	decodeDelimitedArray,
	encodeDelimitedArray,
} from "serialize-query-params";

import type { DashboardListVariableValue } from "../../common/dashboard-spec";

export type DashboardURLParamVariable = Array<{ name: string } & NonNullable<DashboardListVariableValue>>;

const TUPLE_DELIMITER = "|";
const VALUES_ARRAY_DELIMITER = ",";
const ALL_VALUES_DELIMITER = "+";

const ValueTuple = {
	encode: (values: Array<string>) => encodeDelimitedArray(values, TUPLE_DELIMITER) ?? "",
	decode: (value: string) => decodeDelimitedArray(value, TUPLE_DELIMITER) ?? [],
};

const SingleValue = {
	// Handles escaping reserved delimiter characters
	encode: encodeURIComponent,
	decode: (value: string) => {
		try {
			return decodeURIComponent(value);
		} catch {
			return undefined;
		}
	},
};

const ValueTypeEnum = createEnumParam(["v", "a", "e"]);

const ValuesArray = {
	encode: (input: Array<string>) =>
		pipe(
			input,
			// Encode each item of the array to escape commas as they otherwise will match the delimiter
			map(SingleValue.encode),
			// Encode the whole array to a string
			(array) => encodeDelimitedArray(array, VALUES_ARRAY_DELIMITER) ?? "",
		),
	decode: (input: string) =>
		pipe(
			input,
			// Decode the string to an array
			(string) => decodeDelimitedArray(string, VALUES_ARRAY_DELIMITER) ?? [],
			// Decode the items
			map(SingleValue.decode),
		),
};

const AllValues = {
	encode: (values: Array<string>) => encodeDelimitedArray(values, ALL_VALUES_DELIMITER) ?? "",
	decode: (value: string) => decodeDelimitedArray(value, ALL_VALUES_DELIMITER) ?? [],
};

export const dashboardVariablesParam = {
	encode: (dashboardVariables) => {
		if (dashboardVariables === undefined) {
			return undefined;
		}

		return pipe(
			dashboardVariables,
			map((variable) => {
				const { name, type } = variable;

				const encodedName = SingleValue.encode(name);

				if (type === "Value") {
					return ValueTuple.encode([encodedName, "v", SingleValue.encode(variable.value)]);
				}

				if (type === "Values") {
					return ValueTuple.encode([encodedName, "a", ValuesArray.encode(variable.values)]);
				}

				return ValueTuple.encode([encodedName, "e"]);
			}),
			AllValues.encode,
		);
	},

	decode: (input) => {
		if (input == null || isArray(input)) {
			return undefined;
		}

		return pipe(
			input,
			(string) => AllValues.decode(string),
			filter(isPresent),
			map((string) => {
				const array = ValueTuple.decode(string);

				if (!isArray(array)) {
					return undefined;
				}

				const [name, type, value] = [array[0], ValueTypeEnum.decode(array[1]), array[2]];

				if (name == null || type == null) {
					return undefined;
				}

				const decodedName = SingleValue.decode(name);

				if (decodedName == null) {
					return undefined;
				}

				if (type === "e") {
					return { name: decodedName, type: "Everything" };
				}

				if (value == null) {
					return undefined;
				}

				if (type === "a") {
					return {
						name: decodedName,
						type: "Values",
						values: ValuesArray.decode(value),
					};
				}

				return {
					name: decodedName,
					type: "Value",
					value: SingleValue.decode(value),
				};
			}),
			filter(isPresent),
		);
	},
} as QueryParamConfig<undefined | DashboardURLParamVariable>;
